/**
 * AdvancedRetargetingControlsForSmarterCompliancePage
 *
 * Compliance-aware retargeting rule manager. Surfaces per-tenant rules with:
 *   - consent gating + PII exclusion toggles
 *   - jurisdiction scoping + jurisdiction filter
 *   - frequency caps (integer >= 0)
 *   - excluded audience ids
 *   - enabled / disabled filter + toggle
 *
 * GREEN-accent palette (green-600/700/50, zinc neutrals). No emerald/blue/
 * orange/purple/dark themes, no emoji, no competitor names.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAdvancedRetargetingControlsForSmarterComplianceCreate,
	useAdvancedRetargetingControlsForSmarterComplianceDelete,
	useAdvancedRetargetingControlsForSmarterComplianceList,
	useAdvancedRetargetingControlsForSmarterComplianceToggle,
} from "./hooks";
import type { ListFilters, RetargetingRule } from "./api";

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const SELECT = INPUT;
const LABEL = "text-xs font-semibold uppercase tracking-wider text-zinc-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-green-700 bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BADGE_ON =
	"inline-flex items-center rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-700";
const BADGE_OFF =
	"inline-flex items-center rounded-[6px] border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600";

interface CreateFormState {
	rule_key: string;
	name: string;
	jurisdiction: string;
	consent_required: boolean;
	pii_excluded: boolean;
	frequency_cap: string;
	excluded_audience_ids: string;
}

const INITIAL_FORM: CreateFormState = {
	rule_key: "",
	name: "",
	jurisdiction: "",
	consent_required: true,
	pii_excluded: true,
	frequency_cap: "0",
	excluded_audience_ids: "",
};

function parseAudienceIds(raw: string): string[] {
	return raw
		.split(",")
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
}

export function AdvancedRetargetingControlsForSmarterCompliancePage() {
	const [filterJurisdiction, setFilterJurisdiction] = useState("");
	const [filterEnabled, setFilterEnabled] = useState<"" | "0" | "1">("");

	const filters = useMemo<ListFilters>(() => {
		const f: ListFilters = {};
		if (filterJurisdiction.trim())
			f.jurisdiction = filterJurisdiction.trim();
		if (filterEnabled === "0" || filterEnabled === "1")
			f.enabled = filterEnabled;
		return f;
	}, [filterJurisdiction, filterEnabled]);

	const listQuery =
		useAdvancedRetargetingControlsForSmarterComplianceList(filters);
	const createMut =
		useAdvancedRetargetingControlsForSmarterComplianceCreate();
	const deleteMut =
		useAdvancedRetargetingControlsForSmarterComplianceDelete();
	const toggleMut =
		useAdvancedRetargetingControlsForSmarterComplianceToggle();

	const [form, setForm] = useState<CreateFormState>(INITIAL_FORM);

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const rule_key = form.rule_key.trim();
		const name = form.name.trim();
		const jurisdiction = form.jurisdiction.trim();
		if (!rule_key || !name || !jurisdiction) return;
		const cap = Number(form.frequency_cap);
		const frequency_cap = Number.isFinite(cap) && cap >= 0 ? Math.floor(cap) : 0;
		createMut.mutate(
			{
				rule_key,
				name,
				jurisdiction,
				consent_required: form.consent_required,
				pii_excluded: form.pii_excluded,
				excluded_audience_ids: parseAudienceIds(form.excluded_audience_ids),
				frequency_cap,
				enabled: true,
			},
			{
				onSuccess: () => setForm(INITIAL_FORM),
			},
		);
	};

	const handleToggle = (row: RetargetingRule) => {
		toggleMut.mutate({
			rule_key: row.rule_key,
			name: row.name,
			jurisdiction: row.jurisdiction,
			consent_required: row.consent_required,
			pii_excluded: row.pii_excluded,
			excluded_audience_ids: row.excluded_audience_ids,
			frequency_cap: row.frequency_cap,
			enabled: !row.enabled,
		});
	};

	const items = listQuery.data?.items ?? [];

	return (
		<main
			data-testid="advanced-retargeting-controls-for-smarter-compliance-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					Compliance &middot; Retargeting
				</p>
				<h1 className="bg-gradient-to-r from-green-600 to-green-800 bg-clip-text text-3xl font-semibold text-transparent">
					Advanced Retargeting Controls
				</h1>
				<p className="max-w-2xl text-sm text-zinc-600">
					Per-tenant compliance rules that gate retargeting on consent, PII
					exclusion, jurisdiction scoping, audience exclusions, and frequency
					caps.
				</p>
			</header>

			<section className={CARD} aria-label="Rule filters">
				<h2 className="text-base font-semibold text-zinc-900">Filters</h2>
				<div
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3"
					data-testid="advanced-retargeting-controls-for-smarter-compliance-filters"
				>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="filter-jurisdiction">
							Jurisdiction
						</label>
						<input
							id="filter-jurisdiction"
							type="text"
							className={INPUT}
							placeholder="e.g. EU, US-CA, UK"
							value={filterJurisdiction}
							onChange={(e) => setFilterJurisdiction(e.target.value)}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-filter-jurisdiction"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="filter-enabled">
							Status
						</label>
						<select
							id="filter-enabled"
							className={SELECT}
							value={filterEnabled}
							onChange={(e) =>
								setFilterEnabled(
									e.target.value as "" | "0" | "1",
								)
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-filter-enabled"
						>
							<option value="">All</option>
							<option value="1">Enabled only</option>
							<option value="0">Disabled only</option>
						</select>
					</div>
					<div className="flex items-end">
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => {
								setFilterJurisdiction("");
								setFilterEnabled("");
							}}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-filter-reset"
						>
							Reset filters
						</button>
					</div>
				</div>
			</section>

			<section
				className={CARD}
				aria-label="Create retargeting rule"
			>
				<h2 className="text-base font-semibold text-zinc-900">
					New compliance rule
				</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2"
					data-testid="advanced-retargeting-controls-for-smarter-compliance-create-form"
				>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-rule-key">
							Rule key
						</label>
						<input
							id="f-rule-key"
							type="text"
							className={INPUT}
							placeholder="eu-no-minors"
							value={form.rule_key}
							onChange={(e) =>
								setForm((s) => ({ ...s, rule_key: e.target.value }))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-rule-key-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-name">
							Name
						</label>
						<input
							id="f-name"
							type="text"
							className={INPUT}
							placeholder="EU retargeting guardrails"
							value={form.name}
							onChange={(e) =>
								setForm((s) => ({ ...s, name: e.target.value }))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-jurisdiction">
							Jurisdiction
						</label>
						<input
							id="f-jurisdiction"
							type="text"
							className={INPUT}
							placeholder="EU"
							value={form.jurisdiction}
							onChange={(e) =>
								setForm((s) => ({
									...s,
									jurisdiction: e.target.value,
								}))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-jurisdiction-input"
						/>
					</div>
					<div className="flex flex-col gap-1">
						<label className={LABEL} htmlFor="f-cap">
							Frequency cap (per user / window)
						</label>
						<input
							id="f-cap"
							type="number"
							min={0}
							step={1}
							className={INPUT}
							value={form.frequency_cap}
							onChange={(e) =>
								setForm((s) => ({
									...s,
									frequency_cap: e.target.value,
								}))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-frequency-cap-input"
						/>
					</div>
					<div className="flex flex-col gap-1 sm:col-span-2">
						<label className={LABEL} htmlFor="f-excluded">
							Excluded audience ids (comma separated)
						</label>
						<input
							id="f-excluded"
							type="text"
							className={INPUT}
							placeholder="aud_01, aud_minors, aud_do_not_retarget"
							value={form.excluded_audience_ids}
							onChange={(e) =>
								setForm((s) => ({
									...s,
									excluded_audience_ids: e.target.value,
								}))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-excluded-input"
						/>
					</div>
					<label className="flex items-center gap-2 text-sm text-zinc-800">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600"
							checked={form.consent_required}
							onChange={(e) =>
								setForm((s) => ({
									...s,
									consent_required: e.target.checked,
								}))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-consent-required"
						/>
						Consent required
					</label>
					<label className="flex items-center gap-2 text-sm text-zinc-800">
						<input
							type="checkbox"
							className="h-4 w-4 rounded border-zinc-300 text-green-600 focus:ring-green-600"
							checked={form.pii_excluded}
							onChange={(e) =>
								setForm((s) => ({
									...s,
									pii_excluded: e.target.checked,
								}))
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-pii-excluded"
						/>
						PII excluded
					</label>
					<div className="sm:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={
								createMut.isPending ||
								!form.rule_key.trim() ||
								!form.name.trim() ||
								!form.jurisdiction.trim()
							}
							data-testid="advanced-retargeting-controls-for-smarter-compliance-submit"
						>
							{createMut.isPending ? "Saving." : "Create rule"}
						</button>
						{createMut.error ? (
							<p
								className="mt-2 text-xs text-red-600"
								role="alert"
								data-testid="advanced-retargeting-controls-for-smarter-compliance-create-error"
							>
								{createMut.error.message}
							</p>
						) : null}
					</div>
				</form>
			</section>

			<section
				className={CARD}
				aria-label="Retargeting compliance rules"
			>
				<div className="flex items-baseline justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Rules</h2>
					<p
						className="text-xs text-zinc-500"
						data-testid="advanced-retargeting-controls-for-smarter-compliance-count"
					>
						{items.length} result{items.length === 1 ? "" : "s"}
					</p>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advanced-retargeting-controls-for-smarter-compliance-loading"
					>
						Loading rules.
					</p>
				) : listQuery.error ? (
					<p
						className="mt-3 text-sm text-red-600"
						role="alert"
						data-testid="advanced-retargeting-controls-for-smarter-compliance-error"
					>
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="advanced-retargeting-controls-for-smarter-compliance-empty"
					>
						No rules match the current filters.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="advanced-retargeting-controls-for-smarter-compliance-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:justify-between"
								data-testid={`advanced-retargeting-controls-for-smarter-compliance-row-${row.id}`}
							>
								<div className="flex flex-col gap-1">
									<div className="flex flex-wrap items-center gap-2">
										<span
											className="text-sm font-semibold text-zinc-900"
											data-testid={`advanced-retargeting-controls-for-smarter-compliance-name-${row.id}`}
										>
											{row.name}
										</span>
										<span className="rounded-[6px] border border-green-200 bg-green-50 px-2 py-0.5 font-mono text-[11px] text-green-800">
											{row.rule_key}
										</span>
										<span className="rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-700">
											{row.jurisdiction}
										</span>
										<span
											className={row.enabled ? BADGE_ON : BADGE_OFF}
											data-testid={`advanced-retargeting-controls-for-smarter-compliance-status-${row.id}`}
										>
											{row.enabled ? "Enabled" : "Disabled"}
										</span>
									</div>
									<div className="flex flex-wrap gap-3 text-xs text-zinc-600">
										<span>
											Consent:{" "}
											<strong
												className={
													row.consent_required
														? "text-green-700"
														: "text-zinc-500"
												}
											>
												{row.consent_required ? "required" : "not required"}
											</strong>
										</span>
										<span>
											PII:{" "}
											<strong
												className={
													row.pii_excluded
														? "text-green-700"
														: "text-zinc-500"
												}
											>
												{row.pii_excluded ? "excluded" : "included"}
											</strong>
										</span>
										<span>
											Frequency cap:{" "}
											<strong className="text-zinc-800">
												{row.frequency_cap === 0 ? "none" : row.frequency_cap}
											</strong>
										</span>
										<span>
											Excluded audiences:{" "}
											<strong className="text-zinc-800">
												{row.excluded_audience_ids.length}
											</strong>
										</span>
									</div>
								</div>
								<div className="flex shrink-0 items-center gap-2">
									<button
										type="button"
										className={BTN_GHOST}
										onClick={() => handleToggle(row)}
										disabled={toggleMut.isPending}
										data-testid={`advanced-retargeting-controls-for-smarter-compliance-toggle-${row.id}`}
									>
										{row.enabled ? "Disable" : "Enable"}
									</button>
									<button
										type="button"
										className={BTN_GHOST}
										onClick={() => deleteMut.mutate(row.id)}
										disabled={deleteMut.isPending}
										data-testid={`advanced-retargeting-controls-for-smarter-compliance-delete-${row.id}`}
									>
										Delete
									</button>
								</div>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AdvancedRetargetingControlsForSmarterCompliancePage;
